import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore'
import { firebaseDb } from './init'
import { getAllTopics } from './topics'
import moment from 'moment'
import { TrackGoogleAnalyticsEvent } from '@/utils/google-analytics'
import { CourseStartedAction } from '@/utils/google-analytics/events/course'
import { setEnrolledCourses, trackEventMixpanel } from '@/utils/mixpanel'
import { COURSE_SELECTED } from '@/utils/mixpanel/eventNames'
import { getBookedCoursesIdByUser } from './utils'
import { getCalibratedScoresByUserId } from '@/firebase/courses-ts'
import { DEFAULT_MMR } from '@/utils/constants'

const courseRef = (id) => doc(firebaseDb, 'courses', id)

export async function getCourseById(id) {
  const courseDoc = courseRef(id)
  const courseSnapshot = await getDoc(courseDoc)
  const courseData = courseSnapshot.data()

  return { ...courseData, id: courseSnapshot.id, itemType: 'course' }
}

export async function getCoursesWithTopics() {
  const topics = await getAllTopics()
  const uniqueCourseIds = [...new Set(topics.map((topic) => topic.courseId))]

  const coursesWithTopics = await Promise.all(
    uniqueCourseIds.map(async (courseId) => {
      const courseDoc = courseRef(courseId)
      const courseSnapshot = await getDoc(courseDoc)
      if (courseSnapshot.exists()) {
        const courseData = { id: courseId, ...courseSnapshot.data() }
        courseData.topics = topics.filter(
          (topic) => topic.courseId === courseId,
        )
        return courseData
      } else {
        return null
      }
    }),
  )

  return coursesWithTopics.filter((course) => course !== null)
}

export async function addCourseToPurchases({
  userId,
  courseId,
  useCalibratedMMR = true,
}) {
  if (!userId || !courseId) {
    return
  }

  const { title: courseTitle, examLevel } = await getCourseById(courseId)
  const bookeCourseIds = await getBookedCoursesIdByUser(userId)

  // Retrieve the MMR score by examLevel, initially defaulted
  let MMR = DEFAULT_MMR

  if (useCalibratedMMR) {
    const calibratedScores = await getCalibratedScoresByUserId(userId)
    MMR = calibratedScores[examLevel] ?? DEFAULT_MMR // Use calibrated MMR if available
  }

  const bookedCourse = {
    userId,
    courseId,
    avgScore: 0,
    revisitTopics: 0,
    percent: 0,
    status: 'in progress',
    MMR,
  }

  setEnrolledCourses(bookeCourseIds)
  TrackGoogleAnalyticsEvent(CourseStartedAction, CourseStartedAction, {
    userId: bookedCourse.userId,
    courseName: courseTitle,
    courseId: bookedCourse.courseId,
  })

  trackEventMixpanel(COURSE_SELECTED, {
    CourseID: bookedCourse.courseId,
    CourseName: courseTitle,
    UserID: bookedCourse.userId,
  })

  const c = collection(firebaseDb, 'bookedCourses')
  const response = await addDoc(c, {
    ...bookedCourse,
    createdAt: serverTimestamp(),
  })

  return response.id
}

export async function addTemporaryCourses(courses) {
  const c = collection(firebaseDb, 'temporary-courses')
  return await addDoc(c, {
    courses,
    expiresOn: moment().add(2, 'hours').toDate(),
  })
}

export async function fetchTemporaryCourses(id) {
  const d = doc(firebaseDb, 'temporary-courses', id)
  const { courses } = (await getDoc(d)).data()
  return courses
}

export const getAllPurchaseCoursesForCurrentUser = async (userId) => {
  const q = query(
    collection(firebaseDb, 'purchase-courses'),
    where('userId', '==', userId),
  )
  const querySnapshot = await getDocs(q)
  const purchaseCourses = []

  querySnapshot.forEach((doc) => {
    purchaseCourses.push({ id: doc.id, ...doc.data() })
  })

  return purchaseCourses
}

export const isCoursePurchased = async (userId, courseId) => {
  const purchaseCourseRef = collection(firebaseDb, 'bookedCourses')
  const q = query(
    purchaseCourseRef,
    where('userId', '==', userId),
    where('courseId', '==', courseId),
  )
  const querySnapshot = await getDocs(q)
  return querySnapshot?.docs?.length > 0
}
